/**
 * Player state and keyboard shortcuts for the review page (spec #330).
 * Same behaviour as the media fork's `use-video-player`: space/k play, arrows
 * step a frame (shift = 1 s), j/l 5 s, m mute, f fullscreen, 0-9 jump to a tenth.
 * Zustand and Next.js are replaced with local React state.
 * @module web/review/use-video-player
 */
import { useCallback, useEffect, useState, type RefObject } from "react";

export const DEFAULT_FPS = 24;

export type PlayerKeyAction =
  | { type: "toggle" }
  | { type: "step"; seconds: number }
  | { type: "mute" }
  | { type: "fullscreen" }
  | { type: "seekFraction"; fraction: number };

/** The action a key press maps to, null when it is not a player shortcut. */
export function keyToAction(
  e: Pick<KeyboardEvent, "key" | "shiftKey" | "metaKey" | "ctrlKey" | "altKey">,
  fps: number = DEFAULT_FPS,
): PlayerKeyAction | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  const frame = 1 / (fps > 0 ? fps : DEFAULT_FPS);
  switch (e.key) {
    case " ":
    case "k":
    case "K":
      return { type: "toggle" };
    case "ArrowLeft":
      return { type: "step", seconds: e.shiftKey ? -1 : -frame };
    case "ArrowRight":
      return { type: "step", seconds: e.shiftKey ? 1 : frame };
    case "j":
    case "J":
      return { type: "step", seconds: -5 };
    case "l":
    case "L":
      return { type: "step", seconds: 5 };
    case "m":
    case "M":
      return { type: "mute" };
    case "f":
    case "F":
      return { type: "fullscreen" };
    default:
      if (/^[0-9]$/.test(e.key)) return { type: "seekFraction", fraction: Number(e.key) / 10 };
      return null;
  }
}

export function clampTime(t: number, duration: number): number {
  const max = duration > 0 && Number.isFinite(duration) ? duration : Math.max(0, t);
  return Math.min(max, Math.max(0, t));
}

/** True when the key press is aimed at a text field and must not drive the player. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable === true;
}

export interface PlayerApi {
  playing: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  fullscreen: boolean;
  toggle(): void;
  pause(): void;
  seek(seconds: number): void;
  step(seconds: number): void;
  toggleMute(): void;
  toggleFullscreen(): void;
}

export function useVideoPlayer(
  videoRef: RefObject<HTMLVideoElement | null>,
  frameRef: RefObject<HTMLElement | null>,
  opts: { fps?: number; keyboard?: boolean } = {},
): PlayerApi {
  const fps = opts.fps ?? DEFAULT_FPS;
  const keyboard = opts.keyboard ?? true;
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const sync = () => {
      setCurrentTime(v.currentTime);
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
      setPlaying(!v.paused && !v.ended);
      setMuted(v.muted);
    };
    const events = ["timeupdate", "durationchange", "loadedmetadata", "play", "pause", "ended", "seeked", "volumechange"];
    events.forEach((n) => v.addEventListener(n, sync));
    sync();
    return () => events.forEach((n) => v.removeEventListener(n, sync));
  }, [videoRef]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement !== null && document.fullscreenElement === frameRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [frameRef]);

  const pause = useCallback(() => videoRef.current?.pause(), [videoRef]);
  const toggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  }, [videoRef]);
  const seek = useCallback(
    (seconds: number) => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = clampTime(seconds, v.duration);
      setCurrentTime(v.currentTime);
    },
    [videoRef],
  );
  const step = useCallback(
    (seconds: number) => {
      const v = videoRef.current;
      if (!v) return;
      if (Math.abs(seconds) <= 1 / 12) v.pause();
      seek(v.currentTime + seconds);
    },
    [videoRef, seek],
  );
  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (v) v.muted = !v.muted;
  }, [videoRef]);
  const toggleFullscreen = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void frame.requestFullscreen?.().catch(() => undefined);
  }, [frameRef]);

  useEffect(() => {
    if (!keyboard) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const action = keyToAction(e, fps);
      if (!action) return;
      e.preventDefault();
      if (action.type === "toggle") toggle();
      else if (action.type === "step") step(action.seconds);
      else if (action.type === "mute") toggleMute();
      else if (action.type === "fullscreen") toggleFullscreen();
      else if (videoRef.current && Number.isFinite(videoRef.current.duration)) seek(videoRef.current.duration * action.fraction);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, fps, toggle, step, toggleMute, toggleFullscreen, seek, videoRef]);

  return { playing, currentTime, duration, muted, fullscreen, toggle, pause, seek, step, toggleMute, toggleFullscreen };
}
